import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import './Layout.css';
import api from '../api';
import {
  CalendarIcon,
  CloseIcon,
  DashboardIcon,
  GateIcon,
  MapPinIcon,
  MenuIcon,
  PassIcon,
  ShieldIcon,
  SettingsIcon,
  UserIcon,
  SearchIcon,
  MoreVerticalIcon,
  PhoneIcon,
  LogIcon,
  LogOutIcon as SignOutIcon,
  HelpCircleIcon,
  ChevronDownIcon,
  ChartBarIcon
} from './Icons';


export default function Layout({ children }) {
  const { faculty, logout, updateProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [escalationCount, setEscalationCount] = useState(0);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [showSignoutConfirm, setShowSignoutConfirm] = useState(false);
  const [missingPhoneOpen, setMissingPhoneOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [updatingProfile, setUpdatingProfile] = useState(false);

  useEffect(() => {
    if (faculty && !faculty.phone) {
      setMissingPhoneOpen(true);
    }
  }, [faculty]);

  useEffect(() => {
    const fetchCounts = async () => {
      if (!faculty?._id) return;
      try {
        const isAdmin = ['iqac', 'super_admin', 'ao'].includes(faculty?.role);
        const res = await api.get(isAdmin ? '/events/all' : '/events');
        const escalated = (res.data.events || []).filter(e => e.isEscalated && e.escalationStatus !== 'closed');

        let unreadCount = 0;
        escalated.forEach(e => {
          const key = `lastSeen_esc_${String(faculty._id)}_${String(e._id)}`;
          const lastSeen = localStorage.getItem(key);
          const updateTime = new Date(e.updatedAt).getTime();
          const seenTime = lastSeen ? new Date(lastSeen).getTime() : 0;

          if (updateTime > (seenTime + 1000)) { // Add 1s buffer
            unreadCount++;
          }
        });

        setEscalationCount(unreadCount);
      } catch (err) {
        console.error('Failed to fetch escalation count:', err);
      }
    };

    if (faculty) {
      fetchCounts();
      const interval = setInterval(fetchCounts, 10000); // Check every 10s

      const handleUpdate = () => fetchCounts();
      window.addEventListener('escalation_seen', handleUpdate);
      return () => {
        clearInterval(interval);
        window.removeEventListener('escalation_seen', handleUpdate);
      };
    }
  }, [faculty]);

  const toggleGroup = (label) => {
    setOpenGroups(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const navItems = useMemo(() => {
    // Determine user role flags
    const isAdmin = ['iqac', 'super_admin'].includes(faculty?.role);
    const isAO = faculty?.role === 'ao';
    const isFaculty = faculty?.role === 'faculty';
    const isSecurity = faculty?.role === 'security';

    // Security role sees a very limited sidebar
    if (isSecurity) {
      return [
        {
          label: 'Gate security',
          icon: GateIcon,
          isGroup: true,
          items: [
            { path: '/gate/scan', label: 'Scan Entry', icon: SearchIcon },
            { path: '/gate/logs', label: 'Entry Logs', icon: LogIcon }
          ]
        },
        { path: '/help', label: 'Help & Support', icon: HelpCircleIcon }
      ];
    }

    // Start with core items common to all (paths adjusted per role)
    const base = [
      {
        path: isAdmin ? '/admin/dashboard' : (isAO ? '/ao/dashboard' : (isFaculty ? '/faculty/dashboard' : '/dashboard')),
        label: 'Dashboard',
        icon: DashboardIcon
      },
      {
        path: isAdmin ? '/admin/events' : (isAO ? '/ao/events' : (isFaculty ? '/faculty/events' : '/events')),
        label: 'Events',
        icon: MapPinIcon
      }
    ];

    // Add Guest List and Passes for Admins, AO and Faculty
    if (isAdmin || isAO || isFaculty) {
      base.push({
        path: isAdmin ? '/admin/guest-list' : (isAO ? '/ao/guest-list' : '/faculty/guest-list'),
        label: 'Guest List',
        icon: UserIcon
      });
      base.push({
        path: isAdmin ? '/admin/guest-passes' : (isAO ? '/ao/guest-passes' : '/faculty/guest-passes'),
        label: 'Guest Passes',
        icon: PassIcon
      });
    }

    // Add Calendar and Gate Security
    if (isAdmin || isAO) {
      base.push({ 
        path: isAO ? '/ao/calendar' : '/admin/calendar', 
        label: 'Venue Calendar', 
        icon: CalendarIcon 
      });
    }

    if (isAdmin) {
      base.push({
        label: 'Gate Security',
        icon: GateIcon,
        isGroup: true,
        items: [
          { path: '/gate/scan', label: 'Scan Entry', icon: SearchIcon },
          { path: '/gate/logs', label: 'Entry Logs', icon: LogIcon }
        ]
      });
    }

    // Super Admin Management group
    if (faculty?.role === 'super_admin') {
      base.push({
        label: 'Management',
        icon: SettingsIcon,
        isGroup: true,
        items: [
          { path: '/admin/users', label: 'Users', icon: UserIcon },
          { path: '/admin/logs', label: 'Audit logs', icon: LogIcon }
        ]
      });
    }

    base.push({ path: '/help', label: 'Help & Support', icon: HelpCircleIcon });

    return base;
  }, [faculty?.role, escalationCount]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setShowSignoutConfirm(false);
  };

  const handleUpdatePhone = async (e) => {
    e.preventDefault();
    if (!phoneInput || phoneInput.length < 10) return;
    setUpdatingProfile(true);
    try {
      const res = await api.put('/auth/profile', { phone: phoneInput });
      if (res.data.success) {
        updateProfile(res.data.faculty);
        setMissingPhoneOpen(false);
      }
    } catch (err) {
      console.error('Update phone failed:', err);
    } finally {
      setUpdatingProfile(false);
    }
  };

  const handleUpdateAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    const toastId = toast.loading('Uploading photo...');
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result;
      try {
        const res = await api.put('/auth/profile', { avatar: base64String });
        if (res.data.success) {
          updateProfile(res.data.faculty);
          toast.success('Photo updated successfully!', { id: toastId });
        } else {
          toast.error('Failed to update photo', { id: toastId });
        }
      } catch (err) {
        console.error('Update avatar failed:', err);
        toast.error('Error uploading photo', { id: toastId });
      }
    };
    reader.readAsDataURL(file);
  };

  const initials = faculty?.name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() || 'FA';

  return (
    <div className={`layout ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
      <header className="mobile-topbar">
        <button type="button" className="icon-btn mobile-menu-btn" onClick={() => setMobileMenuOpen(true)} aria-label="Open navigation">
          <MenuIcon />
        </button>
        <div className="mobile-brand" />
        <div className="profile-menu-container mobile-only">
          <button 
            type="button" 
            className={`icon-btn profile-more-btn ${profileMenuOpen ? 'active' : ''}`} 
            onClick={() => setProfileMenuOpen(!profileMenuOpen)}
            aria-label="Profile options"
          >
            <MoreVerticalIcon />
          </button>
          {profileMenuOpen && (
            <>
              <div className="profile-menu-overlay" onClick={() => setProfileMenuOpen(false)} />
              <div className="profile-dropdown">
                <button onClick={() => { setProfileModalOpen(true); setProfileMenuOpen(false); }}>
                  <UserIcon style={{ width: 14, height: 14 }} /> Edit Profile
                </button>
                <button className="danger" onClick={() => { setShowSignoutConfirm(true); setProfileMenuOpen(false); }}>
                  <SignOutIcon style={{ width: 14, height: 14 }} /> Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      <div className={`sidebar-backdrop ${mobileMenuOpen ? 'show' : ''}`} onClick={() => setMobileMenuOpen(false)} role="presentation" />

      <aside className="sidebar">
        <div className="sidebar-header university-branded">
          <div className="university-logo-container">
            <img src={`${process.env.PUBLIC_URL}/logo.jpg`} alt="University Logo" className="university-logo" />
          </div>
          <div className="sidebar-brand-row">
            <div className="sidebar-actions">
              <button type="button" className="icon-btn mobile-only" onClick={() => setMobileMenuOpen(false)} aria-label="Close navigation">
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => {
            if (item.isGroup) {
              const isOpen = openGroups[item.label];
              return (
                <div key={item.label} className="nav-group">
                  <button
                    type="button"
                    className={`nav-item nav-group-toggle ${isOpen ? 'expanded' : ''}`}
                    onClick={() => toggleGroup(item.label)}
                  >
                    <span className="nav-icon"><item.icon /></span>
                    <span className="nav-label">{item.label}</span>
                    {item.badge && <span className="nav-badge" style={{ marginRight: '0.5rem' }}>{item.badge}</span>}
                    <span className="nav-chevron"><ChevronDownIcon /></span>
                  </button>
                  {isOpen && (
                    <div className="nav-group-items">
                      {item.items.map(sub => (
                        <NavLink key={sub.path} to={sub.path} end className={({ isActive }) => `nav-item sub-item ${isActive ? 'active' : ''}`}>
                          <span className="nav-icon"><sub.icon /></span>
                          <span className="nav-label">{sub.label}</span>
                          {sub.badge && <span className="nav-badge">{sub.badge}</span>}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink key={item.path} to={item.path} end className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon">
                  <item.icon />
                </span>
                <span className="nav-label">{item.label}</span>
                {item.badge && <span className="nav-badge">{item.badge}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="faculty-avatar">
            {faculty?.avatar ? <img src={faculty.avatar} alt="" className="avatar-img" /> : initials}
          </div>
          <div className="faculty-info">
            <div className="faculty-name">{faculty?.name}</div>
            <div className="faculty-role">{faculty?.role}</div>
          </div>
          
          <div className="profile-menu-container">
            <button 
              type="button" 
              className={`icon-btn profile-more-btn ${profileMenuOpen ? 'active' : ''}`} 
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              title="Profile options"
            >
              <MoreVerticalIcon />
            </button>
            
            {profileMenuOpen && (
              <>
                <div className="profile-menu-overlay" onClick={() => setProfileMenuOpen(false)} />
                <div className="profile-dropdown">
                  <button onClick={() => { setProfileModalOpen(true); setProfileMenuOpen(false); }}>
                    <UserIcon style={{ width: 14, height: 14 }} /> Edit Profile
                  </button>
                  <button className="danger" onClick={() => { setShowSignoutConfirm(true); setProfileMenuOpen(false); }}>
                    <SignOutIcon style={{ width: 14, height: 14 }} /> Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      <main className="main-content">{children}</main>

      {/* Missing Phone Modal */}
      {missingPhoneOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Update Contact Number</h2>
            </div>
            <form onSubmit={handleUpdatePhone} className="modal-body">
              <p style={{ fontSize: '0.875rem', color: 'var(--text-2)', marginBottom: '0.5rem' }}>
                Your phone number is required to display your contact details as a coordinator on Guest Passes.
              </p>
              <div className="field">
                <label>Phone Number</label>
                <input 
                  type="tel" 
                  required 
                  placeholder="e.g. +91 9876543210" 
                  value={phoneInput} 
                  onChange={(e) => setPhoneInput(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="modal-footer" style={{ borderTop: 'none', padding: '0 0 1rem 0' }}>
                <button type="submit" className="btn btn-navy" disabled={updatingProfile}>
                  {updatingProfile ? 'Saving...' : 'Save & Continue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {profileModalOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Your Profile</h2>
              <button className="modal-close" onClick={() => setProfileModalOpen(false)}><CloseIcon /></button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem' }}>
                <div className="faculty-avatar" style={{ width: '80px', height: '80px', fontSize: '24px' }}>
                  {faculty?.avatar ? <img src={faculty.avatar} alt="" className="avatar-img" /> : initials}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <input 
                    type="file" 
                    id="avatar-upload" 
                    hidden 
                    accept="image/*" 
                    onChange={handleUpdateAvatar} 
                  />
                  <label htmlFor="avatar-upload" className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                    Change Photo
                  </label>
                  <p style={{ fontSize: '10px', color: 'var(--text-3)', marginTop: '8px' }}>Max size 2MB</p>
                </div>
              </div>
              
              <div className="grid-1" style={{ display: 'grid', gap: '1rem' }}>
                <div className="field">
                  <label>Display Name</label>
                  <input type="text" readOnly value={faculty?.name} style={{ background: 'var(--gray-50)', cursor: 'not-allowed' }} />
                </div>
                <div className="field">
                  <label>Email Address</label>
                  <input type="email" readOnly value={faculty?.email} style={{ background: 'var(--gray-50)', cursor: 'not-allowed' }} />
                </div>
                <div className="field">
                  <label>Contact Number</label>
                  <input type="text" readOnly value={faculty?.phone} style={{ background: 'var(--gray-50)', cursor: 'not-allowed' }} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setProfileModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Signout Confirmation */}
      {showSignoutConfirm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '340px', borderRadius: '24px' }}>
            <div className="modal-body" style={{ textAlign: 'center', padding: '2rem 1.5rem 1.75rem' }}>
              <div style={{ width: '56px', height: '56px', background: 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)', color: '#e11d48', borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
                <SignOutIcon style={{ width: 28, height: 28 }} />
              </div>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', marginBottom: '0.5rem' }}>Sign Out?</h3>
              
              <div style={{ marginBottom: '1.75rem' }}>
                <p style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.5, marginBottom: '0.5rem' }}>
                  Ready to leave?
                </p>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.375rem 0.75rem', background: 'var(--surface-1)', borderRadius: '10px', border: '1px solid var(--border-1)' }}>
                  <div className="faculty-avatar" style={{ width: '18px', height: '18px', fontSize: '8px' }}>
                    {faculty?.avatar ? <img src={faculty.avatar} alt="" className="avatar-img" /> : initials}
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-1)' }}>{faculty?.email}</span>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="btn btn-ghost" style={{ flex: 1, height: '3.25rem', borderRadius: '12px', fontWeight: 600, fontSize: '14px' }} onClick={() => setShowSignoutConfirm(false)}>Cancel</button>
                <button className="btn btn-red" style={{ flex: 1, height: '3.25rem', borderRadius: '12px', fontWeight: 700, fontSize: '14px', background: '#e11d48', border: 'none' }} onClick={handleLogout}>Sign Out</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
